import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import instance from '@api/axiosInstance';
import { getNextParam } from '@src/utils/getNextParam';
import {
  Container,
  ConfirmationDialog,
  DialogTitle,
  DialogText,
  ButtonContainer,
  CancelButton,
  ConfirmButton,
  LoadingContainer,
} from './styled';
import { logger } from '@src/utils/misc/logger';

interface VerifyAccessResponse {
  success: boolean;
  hasAccess: boolean;
  targetCustomerId?: number;
  targetCustomerName?: string;
  currentCustomerId?: number;
  currentCustomerName?: string;
}

/**
 * CustomerSwitch component handles URL-based customer switching
 *
 * This component requests a customer context switch from the server,
 * which returns a redirect URL. The component then handles client-side
 * navigation to that URL. Uses axios so that the global error interceptors
 * can properly handle 401/403 redirects.
 *
 * Before performing the switch, prompts user for confirmation if switching
 * to a different customer than currently active.
 */
const CustomerSwitch: React.FC = () => {
  const { customerId } = useParams<{ customerId: string }>();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
  const [accessData, setAccessData] = useState<VerifyAccessResponse | null>(null);

  const executeSwitchRequest = useCallback(async () => {
    if (!customerId) return;

    try {
      const nextParam = getNextParam(window.location.search);
      const nextUrl = 'next' in nextParam ? nextParam.next : undefined;
      const params = nextUrl ? { next: nextUrl } : {};

      const traceId = crypto.randomUUID();
      const response = await instance.get(`/customer_switch/${customerId}`, {
        params,
        headers: { 'x-trace-id': traceId },
      });

      if (response.data?.success && response.data?.redirectUrl) {
        window.location.href = response.data.redirectUrl;
      } else {
        const fallbackUrl = nextUrl || '/';
        window.location.href = fallbackUrl;
      }
    } catch (err: any) {
      logger.error('Customer switch error:', err);

      const status = err?.response?.status;

      if (status === 401 || status === 403) {
        const loginUrl = `/login?next=${encodeURIComponent(window.location.pathname + window.location.search)}`;
        window.location.href = loginUrl;
      } else {
        setError(`Failed to switch customer context. Please try again.`);
      }
    }
  }, [customerId, searchParams]);

  useEffect(() => {
    const performSwitch = async () => {
      if (!customerId) return;

      try {
        // Verify user access to target customer using new API
        const traceId = crypto.randomUUID();
        const response = await instance.post<VerifyAccessResponse>('/verify_access', {
          customerId: parseInt(customerId, 10),
          headers: { 'x-trace-id': traceId },
        });

        const data = response.data;

        if (!data.success) {
          setError('Failed to verify account access. Please try again.');
          return;
        }

        if (!data.hasAccess) {
          setError("You don't have access to this account");
          return;
        }

        // Store access data for confirmation dialog
        setAccessData(data);

        // Check if we're switching to a different customer
        if (data.currentCustomerId && data.currentCustomerId.toString() !== customerId) {
          logger.log(
            `Switching from customer ${data.currentCustomerId} to ${customerId}, prompting confirmation`,
          );
          setShowConfirmation(true);
          return; // Don't proceed with switch, wait for user confirmation
        } else {
          // If same customer, proceed with switch
          await executeSwitchRequest();
          return;
        }
      } catch (err: any) {
        logger.error('CustomerSwitch - Failed to verify access', err);
      }
    };

    performSwitch();
  }, [customerId, searchParams, executeSwitchRequest]);

  const handleConfirmSwitch = async () => {
    setShowConfirmation(false);
    await executeSwitchRequest();
  };

  const handleCancelSwitch = () => {
    setShowConfirmation(false);
    window.location.href = window.origin;
  };

  // Show confirmation dialog if switching to different customer
  if (showConfirmation && accessData) {
    return (
      <Container>
        <ConfirmationDialog>
          <DialogTitle>
            Switch Account to <strong>{accessData?.targetCustomerName}</strong>
          </DialogTitle>
          <DialogText>
            This resource belongs to account <strong>{accessData?.targetCustomerName}</strong>.
            <br></br>
            To access the resource you need to switch your current account{' '}
            <strong>{accessData?.currentCustomerName}</strong> to account{' '}
            <strong>{accessData?.targetCustomerName}</strong>.
          </DialogText>
          <ButtonContainer>
            <CancelButton onClick={handleCancelSwitch}>Cancel</CancelButton>
            <ConfirmButton onClick={handleConfirmSwitch}>Switch Customer</ConfirmButton>
          </ButtonContainer>
        </ConfirmationDialog>
      </Container>
    );
  }

  // Show error state if something went wrong
  if (error) {
    return (
      <div style={{ textAlign: 'left', width: '100%' }}>
        Sorry, you don&apos;t have access to this Pulse account
      </div>
    );
  }

  return <LoadingContainer />;
};

export default CustomerSwitch;
